// Shared DOM helpers for managing <head> tags. Used by both
// `usePageMeta` (storefront/admin) and `usePlatformSeo` (platform marketing).
// Keep this file PURE — no React, no settings cache.

export type Restorer = () => void;

export function setMetaTag(
  name: string,
  content: string,
  attr: "name" | "property" = "name",
): Restorer {
  if (typeof document === "undefined") return () => {};
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);
  const previousContent = el ? el.getAttribute("content") : null;
  const created = !el;
  if (!el) {
    if (!content) return () => {};
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return () => {
    if (created) {
      el?.parentNode?.removeChild(el);
    } else if (previousContent !== null) {
      el?.setAttribute("content", previousContent);
    }
  };
}

export function setLinkTag(rel: string, href: string, extraAttrs?: Record<string, string>): Restorer {
  if (typeof document === "undefined" || !href) return () => {};
  const selector = extraAttrs?.hreflang
    ? `link[rel="${rel}"][hreflang="${extraAttrs.hreflang}"]`
    : `link[rel="${rel}"]`;
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  const previousHref = el ? el.getAttribute("href") : null;
  const created = !el;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (extraAttrs) {
      for (const [k, v] of Object.entries(extraAttrs)) el.setAttribute(k, v);
    }
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return () => {
    if (created) {
      el?.parentNode?.removeChild(el);
    } else if (previousHref !== null) {
      el?.setAttribute("href", previousHref);
    }
  };
}

// Idempotent JSON-LD injector keyed by an id attribute on the script tag.
// Replaces existing content when called again with the same id.
export function setJsonLd(id: string, data: unknown): Restorer {
  if (typeof document === "undefined") return () => {};
  let el = document.head.querySelector<HTMLScriptElement>(`script[type="application/ld+json"][data-jsonld-id="${id}"]`);
  const created = !el;
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("data-jsonld-id", id);
    document.head.appendChild(el);
  }
  try {
    el.textContent = JSON.stringify(data);
  } catch {
    el.textContent = "{}";
  }
  return () => {
    if (created) el?.parentNode?.removeChild(el);
  };
}

// Build a BreadcrumbList JSON-LD payload from a URL pathname.
// Path segments are humanised (kebab→Title Case) for the visible name and
// the absolute URL chain is rebuilt from the supplied origin.
//
// Example: origin="https://nexuscart.co", path="/blog/my-post" =>
// 4 items: Home, Blog, My Post (the last item carries the full target URL).
export function buildBreadcrumbsForPath(
  origin: string,
  pathname: string,
  homeName = "Home",
  segmentLabels?: Record<string, string>,
): { "@context": string; "@type": string; itemListElement: Array<Record<string, unknown>> } | null {
  if (typeof origin !== "string" || typeof pathname !== "string") return null;
  const cleanPath = pathname.split("?")[0].split("#")[0];
  const parts = cleanPath.split("/").filter(Boolean);
  if (parts.length === 0) return null; // No breadcrumb on the homepage
  const items: Array<Record<string, unknown>> = [
    { "@type": "ListItem", position: 1, name: homeName, item: `${origin}/` },
  ];
  let acc = "";
  parts.forEach((seg, i) => {
    acc += `/${seg}`;
    const label = segmentLabels?.[seg]
      ?? seg
        .replace(/-/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
    items.push({
      "@type": "ListItem",
      position: i + 2,
      name: label,
      item: `${origin}${acc}`,
    });
  });
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items,
  };
}

// Idempotent script-tag injector. Returns true if injected, false if it
// already existed. Used for analytics scripts (GA4, GTM, pixels).
export function injectExternalScript(opts: {
  id: string;
  src?: string;
  inline?: string;
  async?: boolean;
}): boolean {
  if (typeof document === "undefined") return false;
  if (document.head.querySelector(`script[data-script-id="${opts.id}"]`)) return false;
  const el = document.createElement("script");
  el.setAttribute("data-script-id", opts.id);
  if (opts.src) {
    el.src = opts.src;
    if (opts.async !== false) el.async = true;
  } else if (opts.inline) {
    el.textContent = opts.inline;
  }
  document.head.appendChild(el);
  return true;
}
